import React, { useEffect, useRef, useState } from 'react';
import './App.css';

// ─── Hooks ───────────────────────────────────────────────────────────────────

function useTypingEffect(words) {
  const [text, setText] = useState('');
  const [wordIndex, setWordIndex] = useState(0);
  const [phase, setPhase] = useState('typing');

  useEffect(() => {
    const currentWord = words[wordIndex % words.length];

    if (phase === 'typing') {
      if (text === currentWord) {
        const t = setTimeout(() => setPhase('deleting'), 2200);
        return () => clearTimeout(t);
      }
      const t = setTimeout(() => setText(currentWord.slice(0, text.length + 1)), 95);
      return () => clearTimeout(t);
    }

    if (phase === 'deleting') {
      if (text === '') {
        setWordIndex((i) => (i + 1) % words.length);
        setPhase('typing');
        return;
      }
      const t = setTimeout(() => setText((p) => p.slice(0, -1)), 45);
      return () => clearTimeout(t);
    }
  }, [text, phase, wordIndex, words]);

  return text;
}

function useInView(threshold = 0.12) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold }
    );
    io.observe(el);
    return () => io.unobserve(el);
  }, [threshold]);

  return [ref, visible];
}

// ─── Matrix Canvas ────────────────────────────────────────────────────────────

function MatrixCanvas() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const chars = '0123456789';
    const fontSize = 13;
    const colors = ['#00d4ff', '#7c3aed', '#10b981', '#f59e0b', '#ec4899', '#a78bfa', '#ffffff'];
    let drops = [];

    const reset = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      drops = Array.from(
        { length: Math.floor(canvas.width / fontSize) },
        () => Math.floor(Math.random() * -80)
      );
    };
    reset();

    const onResize = () => reset();
    window.addEventListener('resize', onResize);

    const draw = () => {
      ctx.fillStyle = 'rgba(9, 9, 15, 0.06)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.font = `${fontSize}px 'JetBrains Mono', monospace`;

      drops.forEach((y, i) => {
        ctx.globalAlpha = Math.random() * 0.55 + 0.15;
        ctx.fillStyle = colors[Math.floor(Math.random() * colors.length)];
        ctx.fillText(chars[Math.floor(Math.random() * chars.length)], i * fontSize, y * fontSize);
        if (y * fontSize > canvas.height && Math.random() > 0.975) drops[i] = 0;
        else drops[i]++;
      });
      ctx.globalAlpha = 1;
    };

    const interval = setInterval(draw, 40);
    return () => {
      clearInterval(interval);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  return <canvas ref={canvasRef} className="matrix-canvas" />;
}

// ─── Navigation ───────────────────────────────────────────────────────────────

function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', fn);
    return () => window.removeEventListener('scroll', fn);
  }, []);

  const links = ['About', 'Experience', 'Skills', 'Projects', 'Contact'];

  return (
    <nav className={`nav ${scrolled ? 'nav--scrolled' : ''}`}>
      <a href="#home" className="nav-logo">AD</a>
      <button
        className={`nav-hamburger ${menuOpen ? 'nav-hamburger--open' : ''}`}
        onClick={() => setMenuOpen(!menuOpen)}
        aria-label="Toggle menu"
      >
        <span />
        <span />
        <span />
      </button>
      <ul className={`nav-links ${menuOpen ? 'nav-links--open' : ''}`}>
        {links.map((l) => (
          <li key={l}>
            <a href={`#${l.toLowerCase()}`} onClick={() => setMenuOpen(false)}>
              {l}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}

// ─── Hero ─────────────────────────────────────────────────────────────────────

const TITLES = [
  'Senior Software Engineer',
  'Full Stack Developer',
  'React & Node.js Expert',
  'Cloud Enthusiast',
];

function Hero() {
  const typed = useTypingEffect(TITLES);

  return (
    <section className="hero" id="home">
      <MatrixCanvas />
      <div className="hero__overlay" />
      <div className="hero__content">
        <p className="hero__greeting">Hello, I'm</p>
        <h1 className="hero__name">Arvind Dhindsa</h1>
        <p className="hero__role">
          <span className="hero__typed">{typed}</span>
          <span className="cursor">|</span>
        </p>
        <p className="hero__location">
          <span className="location-dot" />
          Vancouver, BC · Canada
        </p>
        <div className="hero__ctas">
          <a href="#experience" className="btn btn--primary">View My Work</a>
          <a
            href="https://github.com/dhindsa91/"
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn--outline"
          >
            GitHub
          </a>
          <a
            href="https://www.linkedin.com/in/arvind-dhindsa-156b17180/"
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn--outline"
          >
            LinkedIn
          </a>
        </div>
      </div>
      <a href="#about" className="scroll-hint" aria-label="Scroll to About">
        <span className="scroll-hint__label">scroll</span>
        <div className="scroll-hint__line" />
      </a>
    </section>
  );
}

// ─── About ────────────────────────────────────────────────────────────────────

function About() {
  const [ref, visible] = useInView();

  return (
    <section className="section" id="about">
      <div className="container">
        <div ref={ref} className={`about ${visible ? 'in-view' : ''}`}>
          <div className="about__visual">
            <div className="avatar-wrap">
              <div className="avatar-ring" />
              <img
                src={process.env.PUBLIC_URL + '/images/arvind.jpg'}
                alt="Arvind Dhindsa"
                className="avatar-img"
              />
              <div className="avatar-glow" />
            </div>
          </div>
          <div className="about__text">
            <p className="eyebrow">Who I Am</p>
            <h2 className="section-title">About Me</h2>
            <p className="about__bio">
              I'm a <strong>Senior Software Engineer</strong> with 6+ years of experience building scalable
              web and mobile applications. Currently a{' '}
              <strong>Member of Technical Staff at OpusClip</strong>, I work across the full stack
              to ship impactful products at scale.
            </p>
            <p className="about__bio">
              I hold a <strong>BSc in Computer Science</strong> from the University of British Columbia
              (2018) and have honed my craft across some of Vancouver's most exciting tech companies —
              from early-stage startups to high-growth scale-ups.
            </p>
            <div className="about__stats">
              {[
                ['8+', 'Years Experience'],
                ['20+', 'Technologies'],
              ].map(([num, label]) => (
                <div className="stat" key={label}>
                  <span className="stat__num">{num}</span>
                  <span className="stat__label">{label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Experience ───────────────────────────────────────────────────────────────

const EXPERIENCES = [
  {
    company: 'OpusClip',
    role: 'Member of Technical Staff',
    period: 'Dec 2025 – Present',
    location: 'Burnaby, BC · On-site',
    tags: ['React.js', 'Node.js', 'GCP', 'Next.js'],
    accent: '#a78bfa',
  },
  {
    company: 'OpusClip',
    role: 'Senior Software Engineer',
    period: 'Aug 2025 – Dec 2025',
    location: 'Burnaby, BC · On-site',
    tags: ['React.js', 'Node.js', 'TypeScript'],
    accent: '#7c3aed',
  },
  {
    company: 'Seequent',
    role: 'Senior Software Developer',
    period: 'May 2024 – Aug 2025',
    location: 'Vancouver, BC · Hybrid',
    tags: ['OOP', 'Docker', 'TypeScript', 'Python'],
    accent: '#00b4d8',
  },
  {
    company: 'SkipTheDishes',
    role: 'Senior Software Developer',
    period: 'Oct 2022 – Mar 2024',
    location: 'Vancouver, BC · Remote',
    tags: ['TypeScript', 'React', 'GraphQL', 'Java', 'SQL'],
    accent: '#f97316',
  },
  {
    company: 'Jobber',
    role: 'Software Engineer II',
    period: 'Jan 2022 – Sep 2022',
    location: 'Vancouver, BC · Remote',
    tags: ['TypeScript', 'React Native', 'Ruby & Rails', 'GraphQL', 'PostgreSQL'],
    accent: '#10b981',
  },
  {
    company: 'Launchtrip',
    role: 'Software Engineer',
    period: 'Jun 2020 – Jan 2022',
    location: 'Vancouver, BC',
    tags: ['NodeJS', 'TypeScript', 'NestJS', 'Next.js', 'React Native', 'MySQL'],
    accent: '#ec4899',
  },
  {
    company: 'Techno Monkey Media Inc.',
    role: 'Software Developer',
    period: 'Sep 2018 – May 2020',
    location: 'Greater Vancouver',
    tags: ['VueJS', 'NodeJS', 'CouchDB'],
    accent: '#f59e0b',
  },
];

function ExpCard({ exp, index }) {
  const [ref, visible] = useInView(0.08);
  const isGrouped = exp.roles.length > 1;

  return (
    <div
      ref={ref}
      className={`exp-card ${visible ? 'in-view' : ''}`}
      style={{ '--accent': exp.accent, '--delay': `${index * 0.06}s` }}
    >
      <div className="exp-card__dot" />
      <div className="exp-card__body">
        {isGrouped && (
          <div className="exp-card__grouped-header">
            <p className="exp-card__company">{exp.company}</p>
            <span className="exp-card__location">{exp.location}</span>
          </div>
        )}
        {exp.roles.map((r, ri) => (
          <div
            key={ri}
            className={`exp-card__role-block${isGrouped && ri < exp.roles.length - 1 ? ' exp-card__role-block--sep' : ''}`}
          >
            <div className="exp-card__top">
              <div className="exp-card__left">
                <h3 className="exp-card__role">{r.role}</h3>
                {!isGrouped && <p className="exp-card__company">{exp.company}</p>}
              </div>
              <div className="exp-card__right">
                <span className="exp-card__period">{r.period}</span>
                {!isGrouped && <span className="exp-card__location">{exp.location}</span>}
              </div>
            </div>
            <div className="exp-card__tags">
              {r.tags.map((t) => (
                <span key={t} className="chip">{t}</span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Experience() {
  const [ref, visible] = useInView();

  const grouped = EXPERIENCES.reduce((acc, exp) => {
    const last = acc[acc.length - 1];
    if (last && last.company === exp.company) {
      last.roles.push({ role: exp.role, period: exp.period, tags: exp.tags });
    } else {
      acc.push({
        company: exp.company,
        location: exp.location,
        accent: exp.accent,
        roles: [{ role: exp.role, period: exp.period, tags: exp.tags }],
      });
    }
    return acc;
  }, []);

  return (
    <section className="section section--alt" id="experience">
      <div className="container">
        <div ref={ref} className={`section-header ${visible ? 'in-view' : ''}`}>
          <p className="eyebrow">Where I've Worked</p>
          <h2 className="section-title">Experience</h2>
        </div>
        <div className="timeline">
          <div className="timeline__line" />
          {grouped.map((exp, i) => (
            <ExpCard key={exp.company} exp={exp} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Skills ───────────────────────────────────────────────────────────────────

const SKILL_CATS = [
  {
    name: 'Frontend',
    emoji: '🎨',
    skills: ['React.js', 'React Native', 'Next.js', 'TypeScript', 'JavaScript', 'VueJS'],
  },
  {
    name: 'Backend',
    emoji: '⚙️',
    skills: ['Node.js', 'NestJS', 'Java', 'Ruby on Rails', 'Django', 'GraphQL', 'REST APIs'],
  },
  {
    name: 'Databases',
    emoji: '🗄️',
    skills: ['PostgreSQL', 'MySQL', 'CouchDB', 'NoSQL'],
  },
  {
    name: 'Cloud & DevOps',
    emoji: '☁️',
    skills: ['Google Cloud Platform', 'Docker', 'CI/CD', 'Git', 'Terraform'],
  },
];

function SkillCard({ cat, index }) {
  const [ref, visible] = useInView(0.1);

  return (
    <div
      ref={ref}
      className={`skill-card ${visible ? 'in-view' : ''}`}
      style={{ '--delay': `${index * 0.1}s` }}
    >
      <div className="skill-card__head">
        <span className="skill-card__icon">{cat.emoji}</span>
        <h3 className="skill-card__name">{cat.name}</h3>
      </div>
      <div className="skill-card__pills">
        {cat.skills.map((s) => (
          <span key={s} className="pill">{s}</span>
        ))}
      </div>
    </div>
  );
}

function Skills() {
  const [ref, visible] = useInView();

  return (
    <section className="section" id="skills">
      <div className="container">
        <div ref={ref} className={`section-header ${visible ? 'in-view' : ''}`}>
          <p className="eyebrow">What I Know</p>
          <h2 className="section-title">Skills</h2>
        </div>
        <div className="skills-grid">
          {SKILL_CATS.map((cat, i) => (
            <SkillCard key={cat.name} cat={cat} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Projects ────────────────────────────────────────────────────────────────

const PROJECTS = [
  {
    emoji: '💻',
    title: 'GitHub Repository',
    desc: 'A curated collection of full-stack projects spanning Mean Stack, Java Spring, Django, REST APIs, and VueJS. Demonstrates breadth across many frameworks and paradigms.',
    tags: ['Java Spring', 'Django', 'VueJS', 'REST API', 'MEAN Stack'],
    link: 'https://github.com/dhindsa91/',
    cta: 'View on GitHub',
  },
  {
    emoji: '🧩',
    title: 'LeetCode',
    desc: 'Algorithm and data structure solutions across a wide range of difficulty levels — a testament to strong problem-solving and computer science fundamentals.',
    tags: ['Algorithms', 'Data Structures', 'Problem Solving'],
    link: 'https://leetcode.com/dnivra91/',
    cta: 'View Profile',
  },
  {
    emoji: '🚀',
    title: 'Gryp',
    desc: 'A crypto marketplace startup I founded and built solo. Users post and bid on items with cryptocurrency; funds are held in escrow until transactions complete.',
    tags: ['Crypto', 'Escrow', 'Full Stack', 'Marketplace', 'Startup'],
    link: null,
    cta: null,
  },
];

function ProjectCard({ project, index }) {
  const [ref, visible] = useInView(0.1);

  return (
    <div
      ref={ref}
      className={`project-card ${visible ? 'in-view' : ''}`}
      style={{ '--delay': `${index * 0.1}s` }}
    >
      <span className="project-card__emoji">{project.emoji}</span>
      <h3 className="project-card__title">{project.title}</h3>
      <p className="project-card__desc">{project.desc}</p>
      <div className="project-card__tags">
        {project.tags.map((t) => (
          <span key={t} className="pill pill--dim">{t}</span>
        ))}
      </div>
      {project.link && (
        <a
          href={project.link}
          target="_blank"
          rel="noopener noreferrer"
          className="project-card__link"
        >
          {project.cta} →
        </a>
      )}
    </div>
  );
}

function Projects() {
  const [ref, visible] = useInView();

  return (
    <section className="section section--alt" id="projects">
      <div className="container">
        <div ref={ref} className={`section-header ${visible ? 'in-view' : ''}`}>
          <p className="eyebrow">What I've Built</p>
          <h2 className="section-title">Projects</h2>
        </div>
        <div className="projects-grid">
          {PROJECTS.map((p, i) => (
            <ProjectCard key={p.title} project={p} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Contact ─────────────────────────────────────────────────────────────────

function Contact() {
  const [ref, visible] = useInView();

  return (
    <section className="section contact-section" id="contact">
      <div className="container">
        <div ref={ref} className={`contact ${visible ? 'in-view' : ''}`}>
          <p className="eyebrow">Let's Talk</p>
          <h2 className="section-title">Get In Touch</h2>
          <p className="contact__text">
            Open to new opportunities and always happy to connect.
            <br />
            Feel free to reach out on LinkedIn.
          </p>
          <div className="contact__links">
            <a
              href="https://www.linkedin.com/in/arvind-dhindsa-156b17180/"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn--primary btn--lg"
            >
              LinkedIn
            </a>
            <a
              href="https://github.com/dhindsa91/"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn--outline btn--lg"
            >
              GitHub
            </a>
            <a
              href="https://leetcode.com/dnivra91/"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn--outline btn--lg"
            >
              LeetCode
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Footer ───────────────────────────────────────────────────────────────────

function Footer() {
  return (
    <footer className="footer">
      <p>Designed &amp; built by Arvind Dhindsa · {new Date().getFullYear()}</p>
    </footer>
  );
}

// ─── App ──────────────────────────────────────────────────────────────────────

function App() {
  return (
    <>
      <Nav />
      <main>
        <Hero />
        <About />
        <Experience />
        <Skills />
        <Projects />
        <Contact />
      </main>
      <Footer />
    </>
  );
}

export default App;
